import React from "react";
import { connect } from "react-redux";
import { actions, getters } from "EventLight/Sessions/AddSessionModal/model";
import { Div, Span, InfoIcon } from "components/Base";
import * as R from "ramda";
import { Panel, PanelTitle } from "EventLight/Common/FieldLayout";
import {
  FIELD_IDS,
  LOCATION_TYPE_IDS,
  VIRTUAL_TYPES_LABEL
} from "EventLight/Sessions/AddSessionModal/constants";
import { Link } from "react-router";
import { Dropdown, DropdownAsync, Input } from "ui-kit/Form/View";
import {
  getLocationOptions,
  canEditSessions
} from "EventLight/Sessions/AddSessionModal/selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import { makeStyles } from "@material-ui/core/styles";
import CanUserDo from "components/Global/Security/CanUserDo";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";
import CustomFields from "EventLight/Sessions/Page/CustomFields/View";
import { noop } from "utils/General";
import MultiValueLabel from "./DecorateMultiValueLabel";
import Option from "EventLight/Sessions/Page/View/OptionSpeakerDropdown";

const useStyles = makeStyles(() => ({
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignContent: "center",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 25,
    marginBottom: 20
  },
  fieldWidth1_2: {
    width: "50%"
  },
  fieldWidth1_1: {
    width: "100%"
  }
}));

export const InputWrapperRow = ({ children, ...props }) => {
  return (
    <Div display="row" {...props}>
      {children}
    </Div>
  );
};

const Label = ({ children, tooltip }) => {
  return (
    <Div color="black" fw={3} fs={3} display="row.flex-start.center" mb={2}>
      {children}
      {tooltip ? <InfoIcon ml={1} color="gray5" tooltip={tooltip} /> : null}
    </Div>
  );
};

const decorate = connect(
  state => ({
    data: getters.data(state),
    locationOptions: getLocationOptions(state),
    eventId: getEventId(state),
    canEdit: canEditSessions(state),
    isSubsession: getters.isSubsession(state),
    speakersList: getters.speakersList(state)
  }),
  {
    showBulkEditSpeakersModal: actions.showBulkEditSpeakersModal
  }
);

const Other = ({
  data = {},
  locationOptions = [],
  eventId = "",
  canEdit,
  isSubsession,
  showBulkEditSpeakersModal = noop,
  speakersList = []
}) => {
  const classes = useStyles();
  const { id = null } = data || {};
  return (
    <>
      {/* Location */}
      <Panel>
        <PanelTitle description="Select where this session will be held. You can also specify an external URL (e.g. for Zoom or directing people to a website for a virtual session).">
          Location Details
        </PanelTitle>

        <div className={classes.row}>
          {/* location */}
          <div style={{ width: "20%" }}>
            <Label tooltip="Select the type of location for where your session will be held">
              Location
            </Label>
            <InputWrapperRow display="row">
              <Dropdown
                fieldId={FIELD_IDS.LOCATION}
                iniValue={R.prop(FIELD_IDS.LOCATION)(data)}
                options={locationOptions}
                size="big"
                width={"100%"}
                style={{ width: "100%" }}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
          <div style={{ width: "80%" }}>
            {/* room */}
            {R.prop(FIELD_IDS.LOCATION)(data) === LOCATION_TYPE_IDS.ROOM ? (
              <>
                <Div mt={2} fs={1} display="row.space-between.center">
                  <Label tooltip="Select what room this session is being held at">
                    Room
                  </Label>
                  <CanUserDo
                    action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
                  >
                    <Link
                      to={`/event-light/${eventId}/sessions/settings/rooms`}
                      target="_blank"
                    >
                      <Span color="primary7">Manage your rooms</Span>
                    </Link>
                  </CanUserDo>
                </Div>

                <InputWrapperRow display="row">
                  <DropdownAsync
                    fieldId={FIELD_IDS.LOCATION_ROOM}
                    iniValue={
                      R.prop(FIELD_IDS.LOCATION_ROOM)(data)
                        ? {
                            value: R.prop(FIELD_IDS.LOCATION_ROOM)(data),
                            label: R.path(["location_room", "name"])(data)
                          }
                        : R.prop(FIELD_IDS.ROOM)(data)
                        ? {
                            label: R.prop(FIELD_IDS.ROOM)(data),
                            no_value: true
                          }
                        : []
                    }
                    multipleSelect={false}
                    width={"100%"}
                    placeholder="Select a room..."
                    loadOptionsOnFocus={true}
                    loadToPopulate={!R.prop(FIELD_IDS.LOCATION_ROOM)(data)}
                    disabled={!canEdit}
                  />
                </InputWrapperRow>
              </>
            ) : null}

            {/* Virtual */}
            {R.prop(FIELD_IDS.LOCATION)(data) === LOCATION_TYPE_IDS.VIRTUAL && (
              <>
                <Div mt={2} fs={1} display="row.space-between.center">
                  <Label tooltip="Choose a type for your virtual session, or add an external link">
                    Type
                  </Label>
                </Div>

                <InputWrapperRow display="row">
                  <DropdownAsync
                    fieldId={FIELD_IDS.VIRTUAL}
                    iniValue={
                      R.prop(FIELD_IDS.VIRTUAL)(data)
                        ? {
                            value: R.path(
                              ["session_location_type", "value"],
                              data
                            ),
                            label: R.prop(
                              R.path(["session_location_type", "value"], data),
                              VIRTUAL_TYPES_LABEL
                            )
                          }
                        : []
                    }
                    multipleSelect={false}
                    width={"100%"}
                    placeholder="Select a virtual location type…"
                    loadOptionsOnFocus={true}
                    disabled={!canEdit}
                  />
                </InputWrapperRow>
              </>
            )}
          </div>
        </div>

        {(R.prop(FIELD_IDS.LOCATION)(data) === LOCATION_TYPE_IDS.VIRTUAL &&
          R.prop(FIELD_IDS.VIRTUAL)(data) === LOCATION_TYPE_IDS.EXTERNAL) ||
        R.path(["value"], data.session_location_type) ===
          LOCATION_TYPE_IDS.EXTERNAL ? (
          <>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Enter a URL (e.g. Zoom URL) to link people to when this session is live">
                External URL
              </Label>
            </Div>

            <InputWrapperRow display="row">
              <Input
                style={{ width: "100%" }}
                fieldId={FIELD_IDS.LOCATION_EXTERNAL_URL}
                iniValue={R.prop(FIELD_IDS.LOCATION_EXTERNAL_URL)(data)}
                size="big"
                inputType="outline"
                placeholder="Enter an external URL for this session"
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </>
        ) : null}
      </Panel>

      {/* Session Participants */}
      <Panel>
        <PanelTitle description="Manage speakers, chairpersons and moderators that will participate in this session.">
          Session Participants
        </PanelTitle>
        <div className={classes.row}>
          {/* speakers */}
          <div className={classes.fieldWidth1_1}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Div display="row.space-between.center">
                <Label tooltip="Select one or more speakers for this session.">
                  Speakers
                </Label>
                {speakersList.length > 0 && (
                  <Span
                    ml={2}
                    mb={1}
                    color="primary7"
                    onClick={showBulkEditSpeakersModal}
                  >
                    Bulk edit
                  </Span>
                )}
              </Div>
              <CanUserDo action={`${STANDARD_MODULE_IDS.speakers.id}_read`}>
                <Link
                  onlyActiveOnIndex
                  to={`/event-light/${eventId}/speakers`}
                  target="_blank"
                >
                  <Span color="primary7">Manage your speakers</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.SPEAKERS}
                iniValue={R.prop(FIELD_IDS.SPEAKERS)(data)}
                multipleSelect={true}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one or more speakers..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
                components={{
                  MultiValueLabel,
                  Option
                }}
              />
            </InputWrapperRow>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.fieldWidth1_2}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label>Chairperson</Label>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.CHAIRPERSONS}
                iniValue={R.prop(FIELD_IDS.CHAIRPERSONS)(data)}
                multipleSelect={true}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one or more speakers..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
          <div className={classes.fieldWidth1_2}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label>Moderator</Label>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.MODERATORS}
                iniValue={R.prop(FIELD_IDS.MODERATORS)(data)}
                multipleSelect={true}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one or more speakers..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.fieldWidth1_2}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label>Session Submitter</Label>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.SESSION_SUBMITTER}
                iniValue={R.prop(FIELD_IDS.SESSION_SUBMITTER)(data)}
                multipleSelect={false}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one Session Submitter..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
          <div className={classes.fieldWidth1_2}>
            {/* Session Admin Users */}
          </div>
        </div>
      </Panel>

      {/* Recap */}
      <Panel>
        <PanelTitle description="Select sponsors and exhibitors that this session relates to">
          Sponsors & Exhibitors
        </PanelTitle>
        <div className={classes.row}>
          {/* speakers */}
          <div className={classes.fieldWidth1_2}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Select one or more sponsors for this session.">
                Sponsors
              </Label>
              <CanUserDo action={`${STANDARD_MODULE_IDS.sponsors.id}_read`}>
                <Link to={`/event-light/${eventId}/sponsors`} target="_blank">
                  <Span color="primary7">Manage your sponsors</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.SPONSORS}
                iniValue={R.prop(FIELD_IDS.SPONSORS)(data)}
                multipleSelect={true}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one or more sponsors..."
                loadOptionsOnFocus={true}
                menuPlacement={"top"}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>

          <div className={classes.fieldWidth1_2}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Select one or more exhibitors for this session.">
                Exhibitors
              </Label>
              <CanUserDo action={`${STANDARD_MODULE_IDS.exhibitors.id}_read`}>
                <Link to={`/event-light/${eventId}/exhibitors`} target="_blank">
                  <Span color="primary7">Manage your exhibitors</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.EXHIBITORS}
                iniValue={R.prop(FIELD_IDS.EXHIBITORS)(data)}
                multipleSelect={true}
                menuHeight={250}
                style={{ width: "100%" }}
                placeholder="Select one or more exhibitors..."
                loadOptionsOnFocus={true}
                menuPlacement={"top"}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
        </div>
      </Panel>

      {!isSubsession && id && (
        <>
          <CustomFields
            sessionId={data.id}
            sessionRecordId={data.record_id}
            disabled={!canEdit}
          />
          <Div mb="20px" />
        </>
      )}
    </>
  );
};

export default decorate(Other);
